import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { ouraApi } from "../api/oura";
import { isOuraUIEnabled } from "../utils/features";
import { dailyCheckinApi } from "../api/dailyCheckin";
import { protocolInputsApi } from "../api/protocolInputs";
import { featureFlagsApi } from "../api/featureFlags";
import { foundationsApi } from "../api/foundations";
import { syncHealthKitToday } from "../services/healthkit";
import FoundationsPrompt from "../components/foundations/FoundationsPrompt";
import BreathingGuide from "../components/BreathingGuide";
import { resurfacedIntention } from "../utils/screenFollowthrough";
import { timeWindow } from "../utils/activeProtocol";
import { presentedScore } from "../utils/presentedScore";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import HistoricalCheckIn from "../components/HistoricalCheckIn";
import { Wordmark } from "../components/Wordmark";

// Local YYYY-MM-DD for the user's calendar. Matches todayKey() below
// but lives at module scope so the historical-view gate can use it
// before the component body runs.
function _localTodayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Contextual journal prompt based on local time of day. Drives the
// brain-dump heading when JOURNAL_FIRST_ENABLED is on. Boundaries match
// natural ritual checkpoints — first thing in the morning, midday
// reset, evening reflection — and roll over at the user's clock so the
// copy lands as "right now" instead of "this morning" past noon.
function timeOfDayPrompt() {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) {
    return { heading: "How are you walking\ninto today?", kicker: "Morning reflection" };
  }
  if (h >= 11 && h < 17) {
    return { heading: "What’s the state of\nthings right now?", kicker: "Midday reset" };
  }
  return { heading: "How did today\nactually go?", kicker: "Evening reflection" };
}

// Coerce an extraction-API response into 1–10 slider scores.
// Returns null if no domain produced a usable score — the caller treats
// null as "fall back silently to slider-only mode" per the kill-switch
// behaviour spec'd for journal-first.
function extractionToScores(ext) {
  if (!ext) return null;
  const toSlider = (v) => {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0 || n > 1) return null;
    // Map 0.0–1.0 → 1–10 with floor at 1 (zero is never a valid
    // slider position; the schema's enum bottom is 1).
    return Math.max(1, Math.round(n * 10));
  };
  const out = {
    BIO:   toSlider(ext.bio_score),
    PSYCH: toSlider(ext.psych_score),
    BEH:   toSlider(ext.beh_score),
  };
  if (out.BIO == null && out.PSYCH == null && out.BEH == null) return null;
  return out;
}

// Per-domain check-in copy \u2014 lives here since it's CheckIn-specific
// content, not part of the shared domain vocabulary. Order, code,
// label, and id (numbering) all derive from DOMAIN_META so this can't
// drift from the canonical order again.
const DOMAIN_COPY = {
  PSYCH: {
    question: "How clear and settled does your mind feel right now?",
    low: "foggy", high: "clear-headed",
    hint: "Consider your self-talk, how present or scattered you\u2019ve been, and whether your emotional tone has felt stable or reactive.",
  },
  BIO: {
    question: "How does your body feel right now?",
    low: "needing a nap", high: "feeling 100%",
    hint: "Think about your energy, sleep quality, any physical tension, and how clear-headed you feel.",
  },
  BEH: {
    question: "How aligned have your actions been with your intentions?",
    low: "off track", high: "right on track",
    hint: "Reflect on your follow-through, your daily rhythm, and whether you\u2019ve been doing what you said you would.",
  },
};

const DOMAINS = DOMAIN_META.map((d, i) => ({
  code: d.code,
  label: d.display,
  id: String(i + 1).padStart(2, "0"),
  ...DOMAIN_COPY[d.code],
}));

const STEP_LABELS = ["Overview", "Reflect", "Domains", "Summary"];

// ── Resume / persistence ──────────────────────────────────────────────
// Progress is stored per-calendar-day. On mount we restore state from
// localStorage so that navigating away or a crash mid-protocol returns
// the user exactly where they left off — not at the beginning.
const STORAGE_KEY = "haiven.checkin.v1";
// Separate key — never cleared on daily rotation. Stamped once, the first
// time /rci/compute succeeds on this device. Used to decide whether the
// user is a first-ever check-inner: if the flag is missing, the breathing
// intro must play, even when today's in-flight localStorage resume entry
// exists. Surviving past daily rotation is intentional — once you've
// submitted once, you don't need to be slowed back down on every re-entry.
const EVER_SUBMITTED_KEY = "haiven.checkin.everSubmitted";
// User-local YYYY-MM-DD. ``toISOString().slice(0,10)`` cuts at UTC midnight,
// which on the western US flips ``today`` early — leaking yesterday's
// brain dump into today's check-in screen at 5pm Mountain. Mirrors the
// fix from Protocol.jsx (commit 963fe0c).
const todayKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Same shape as todayKey() but for an arbitrary Date — used to derive
// ``window_start`` (today − 14 days) without going through UTC ISO.
function localDateKeyFor(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.date !== todayKey()) return null;
    return data;
  } catch {
    return null;
  }
}

function saveProgress(patch) {
  try {
    const prev = loadProgress() || {};
    const next = { ...prev, ...patch, date: todayKey() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota errors / private mode — fail silently */
  }
}

function clearProgress() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

function hasEverSubmitted() {
  try { return localStorage.getItem(EVER_SUBMITTED_KEY) === "true"; }
  catch { return false; }
}

function markEverSubmitted() {
  try { localStorage.setItem(EVER_SUBMITTED_KEY, "true"); }
  catch { /* noop */ }
}

// ── Breathing entry animation ─────────────────────────────────────────
// Shown once per session before the first question appears — the moment
// that brings the user back into their body before anything is asked of
// them. Same guide as the Evening Wind-Down (BreathingGuide: 4s in →
// 6s out, one smooth continuous cycle). One full cycle, then
// auto-advances. Tap anywhere to skip.
function BreathingIntro({ onComplete }) {
  return (
    <div
      onClick={onComplete}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: P.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 40,
        cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <BreathingGuide
        suggestedCycles={1}
        showCounter={false}
        onCyclesComplete={onComplete}
      />

      <div style={{
        fontSize: 11, color: P.textMute, letterSpacing: "0.18em",
        textTransform: "uppercase", fontWeight: 500,
      }}>
        Tap to skip
      </div>
    </div>
  );
}

// Microphone icon — used below the brain-dump input.
function MicIcon({ size = 16, color = P.primary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

// Large RCI Ring with count-up animation (local to CheckIn)
function BigRing({ score, size = 200, animate = true, indeterminate = false }) {
  const stroke = 7;
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const [displayed, setDisplayed] = useState(animate ? 0 : score);

  useEffect(() => {
    if (!animate || indeterminate) return;
    let start = null;
    const duration = 1400;
    const ease = t => 1 - Math.pow(1 - t, 3);
    const tick = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setDisplayed(Math.round(ease(progress) * score));
      if (progress < 1) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score, animate, indeterminate]);

  const arcValue = indeterminate ? 15 : displayed;
  const offset = circ - (arcValue / 100) * circ;

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={P.border} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={indeterminate ? P.textFaint : P.primary} strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: animate ? "none" : "stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 2,
      }}>
        <div style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 52, fontStyle: "italic", color: indeterminate ? P.textFaint : P.text,
          lineHeight: 1, fontWeight: 400,
        }}>
          {indeterminate ? "\u2014" : displayed}
        </div>
        <div style={{
          fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
          color: P.primaryMid, fontWeight: 500,
        }}>
          Signal
        </div>
      </div>
    </div>
  );
}

const DOMAIN_DESCRIPTIONS = {
  BIO: "Your energy, sleep quality, physical tension, and how clear-headed you feel.",
  PSYCH: "Your self-talk, presence, emotional stability, and whether your mind feels settled or reactive.",
  BEH: "Your follow-through, daily rhythm, and whether you\u2019ve been doing what you said you would.",
};

// Domain Slider (custom touch/mouse drag — no native <input>)
function DomainSlider({ domain, value, onChange, wearable, isBaseline = false, touched = true }) {
  const w = wearable;
  const diff = Math.abs(value - w.detected);
  const hasSensor = domain === "BIO";
  const pct = (v) => `${v * 10}%`;
  const trackRef = useRef(null);
  const dragging = useRef(false);

  const clampValue = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return value;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.round(Math.max(1, Math.min(10, ratio * 10)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    onChange(clampValue(x));
  };

  useEffect(() => {
    const onPointerMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      onChange(clampValue(x));
    };
    const onPointerUp = () => { dragging.current = false; };

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
    };
  }, [onChange]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      background: P.surface, borderRadius: 20,
      padding: "22px 22px 20px",
      boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
      marginBottom: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, color: P.primary, letterSpacing: "0.18em",
            textTransform: "uppercase", fontWeight: 500, marginBottom: 5 }}>
            {domain.id} &middot; {DOMAIN_WORD[domain.code] || domain.code}
          </div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 22, fontStyle: "italic", color: P.text, lineHeight: 1,
          }}>
            {domain.label}
          </div>
        </div>
        <div style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 34, fontStyle: "italic",
          color: isBaseline && !touched ? P.textFaint : P.text,
          lineHeight: 1,
        }}>
          {isBaseline && !touched ? "\u2014" : value}
        </div>
      </div>

      <p style={{ fontSize: 13, color: P.textMid, lineHeight: 1.65,
        fontWeight: 300, marginBottom: 18, fontStyle: "italic" }}>
        {domain.question}
      </p>

      {/* Custom slider */}
      <div style={{ position: "relative", marginBottom: 10 }}>
        {hasSensor && !isBaseline && (
          <div style={{
            position: "absolute", top: -18, left: pct(w.detected),
            transform: "translateX(-50%)",
            fontSize: 9, color: P.primaryMid, letterSpacing: "0.06em",
            fontWeight: 500, whiteSpace: "nowrap",
          }}>
            &uarr; sensor
          </div>
        )}

        {/* Hit area — 56px tall, invisible, captures all pointer events */}
        <div
          ref={trackRef}
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          style={{
            position: "relative", width: "100%", height: 56,
            cursor: "pointer", touchAction: "none",
            display: "flex", alignItems: "center",
          }}
        >
          {/* Track background */}
          <div style={{
            position: "absolute", left: 0, right: 0,
            height: 6, borderRadius: 99, background: P.surfaceAlt,
          }}>
            {/* Filled portion */}
            <div style={{
              height: "100%", width: pct(value), borderRadius: 99,
              background: `linear-gradient(90deg, ${P.primary}, ${P.primaryMid})`,
              // Deliberate ref read: suppress the width transition only while
              // actively dragging (kept a ref to avoid re-rendering on every
              // pointer move). Re-render is already driven by `value` changing.
              // eslint-disable-next-line react-hooks/refs
              transition: dragging.current ? "none" : "width 0.1s ease",
            }} />
          </div>

          {/* Sensor dot */}
          {hasSensor && !isBaseline && (
            <div style={{
              position: "absolute", left: pct(w.detected),
              transform: "translateX(-50%)",
              width: 8, height: 8, borderRadius: "50%",
              background: P.primarySoft, border: `2px solid ${P.primaryMid}`,
              zIndex: 1,
            }} />
          )}

          {/* Thumb pill */}
          <div style={{
            position: "absolute",
            left: pct(value),
            transform: "translateX(-50%)",
            width: 44, height: 28, borderRadius: 14,
            background: P.dark,
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2, userSelect: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          }}>
            <span style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 14, fontStyle: "italic", color: "#F2EDE3",
              lineHeight: 1, fontWeight: 400,
            }}>
              {isBaseline && !touched ? "\u2014" : value}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 10, color: P.textFaint }}>1 · {domain.low}</span>
        <span style={{ fontSize: 10, color: P.textFaint }}>{domain.high} · 10</span>
      </div>

      {isBaseline ? (
        <p style={{ fontSize: 12.5, color: P.textMute, fontWeight: 300, lineHeight: 1.6 }}>
          {DOMAIN_DESCRIPTIONS[domain.code]}
        </p>
      ) : (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 12px", background: P.surfaceAlt,
          borderRadius: 10,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%",
            background: hasSensor ? P.primary : P.textFaint, flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: P.textMid, fontWeight: 300, lineHeight: 1.4 }}>
            {w.label}
          </span>
        </div>
      )}

      {hasSensor && !isBaseline && diff >= 2 && (
        <div style={{
          marginTop: 10, padding: "9px 12px",
          background: "#E4DDD0", borderRadius: 10,
          fontSize: 11, color: P.primaryMid, lineHeight: 1.5, fontWeight: 300,
        }}>
          Your sense of this sits {diff} points from the sensor reading. Neither is wrong — it&rsquo;s just something to notice.
        </div>
      )}
    </div>
  );
}

// Progress Dots
function ProgressDots({ step, total }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === step ? 20 : 6, height: 6, borderRadius: 99,
          background: i === step ? P.primary : i < step ? P.primarySoft : P.border,
          transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        }} />
      ))}
    </div>
  );
}

// Styles
const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .checkin { font-family: 'DM Sans', sans-serif; background: ${P.bg};
    min-height: 100vh; color: ${P.text}; max-width: 420px; margin: 0 auto; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .step-enter { animation: fadeUp 0.42s ease forwards; }
  .fu { animation: fadeUp 0.5s ease forwards; }
  .d1 { animation-delay: 0.05s; opacity: 0; }
  .d2 { animation-delay: 0.18s; opacity: 0; }
  .d3 { animation-delay: 0.32s; opacity: 0; }
  .d4 { animation-delay: 0.46s; opacity: 0; }

  textarea {
    width: 100%; background: ${P.surface}; border: 1.5px solid ${P.border};
    border-radius: 16px; padding: 18px; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 300; color: ${P.text}; line-height: 1.75;
    resize: none; outline: none; transition: border-color 0.2s;
  }
  textarea:focus { border-color: ${P.primaryMid}; }
  textarea::placeholder { color: ${P.textFaint}; font-style: italic; }

  .btn-primary {
    width: 100%; padding: 17px; background: ${P.dark};
    border: none; border-radius: 16px; cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px; font-style: italic; color: ${P.bg};
    transition: opacity 0.15s; letter-spacing: 0.01em;
  }
  .btn-primary:hover { opacity: 0.85; }

  .btn-ghost {
    background: none; border: 1.5px solid ${P.border};
    border-radius: 16px; padding: 13px 24px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    color: ${P.textMid}; transition: all 0.15s;
  }
  .btn-ghost:hover { border-color: ${P.primaryMid}; color: ${P.primary}; }
`;

export default function CheckIn() {
  const [searchParams] = useSearchParams();

  // Historical-view gate. Feed CTAs route here with ?date=YYYY-MM-DD
  // pointing at a specific past day. When the date resolves to
  // anything other than today's local calendar date we render a
  // read-only summary screen instead of the live check-in flow —
  // the user came to look at what they did, not to overwrite it.
  // Today (or a missing param) falls through to the normal flow.
  const dateParam = searchParams.get("date");
  if (dateParam && dateParam !== _localTodayKey()) {
    return <HistoricalCheckIn date={dateParam} />;
  }
  // All check-in hooks live in LiveCheckIn so none is ever called after
  // the early return above (React rules-of-hooks). The parent only routes
  // historical vs. live; the child owns the live flow and all its hooks.
  return <LiveCheckIn />;
}

function LiveCheckIn() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  // Null or today's key here — the parent already returned for any past
  // date. Used by the auto-redirect guard below.
  const dateParam = searchParams.get("date");

  // iOS native shell: freshen HealthKit metrics while the user writes,
  // so the /rci/compute fired at submit sees today's sensor data.
  // Throttled + single-flight inside the service; no-op off iOS or
  // when Apple Health isn't connected.
  useEffect(() => {
    syncHealthKitToday(token);
  }, [token]);

  // Restore any in-flight progress from today. We skip breathing only if
  // (a) today's localStorage entry exists AND (b) the user has previously
  // submitted a check-in on this device. A brand-new user with stale or
  // partial localStorage state still gets the breathing intro on their
  // first ever check-in — it's deliberately not skippable.
  const saved = typeof window !== "undefined" ? loadProgress() : null;
  const skipBreathing = typeof window !== "undefined"
    && saved
    && hasEverSubmitted();

  const [phase, setPhase] = useState(skipBreathing ? "flow" : "breathing");
  const [step, setStep] = useState(saved?.step ?? 0);
  const [reflection, setReflection] = useState(saved?.reflection ?? "");
  const [scores, setScores] = useState(saved?.scores ?? { BIO: 5, PSYCH: 5, BEH: 5 });
  const [touched, setTouched] = useState(saved?.touched ?? { BIO: false, PSYCH: false, BEH: false });
  const [submitted, setSubmitted] = useState(false);
  const [computing, setComputing] = useState(false);
  const [computeError, setComputeError] = useState("");
  const [computeResult, setComputeResult] = useState(saved?.computeResult ?? null);
  const [baseline, setBaseline] = useState(null);
  const [baselineLoading, setBaselineLoading] = useState(true);
  const [wearableData, setWearableData] = useState(null);
  const [ouraConnected, setOuraConnected] = useState(false);
  const [savingBrainDump, setSavingBrainDump] = useState(false);
  // Foundations — the daily depth prompt appended to the check-in. Null
  // when nothing is due today (or the fetch failed); the flow renders
  // normally in that case. Never blocks the check-in.
  const [foundationsPrompt, setFoundationsPrompt] = useState(null);
  const [foundationsComplete, setFoundationsComplete] = useState(false);
  // "HAIVEN NOTICED" card — populated by a single delayed fetch after the
  // user submits their brain dump. null until we know whether extraction
  // produced anything; falsy → render nothing.
  const [extractionSummary, setExtractionSummary] = useState(null);
  // "Yesterday you said: [X]" resurface on Step 0. null = unchecked,
  // "" = checked and nothing to show (both render nothing); string = render.
  const [yesterdayIntention, setYesterdayIntention] = useState(null);

  // Journal-first feature flag (server-driven, runtime kill switch).
  // ``null`` while loading; flips to bool once /feature-flags responds.
  // We default to ``true`` so the new flow is the optimistic path for
  // the F&F pilot, but if the fetch fails we fall back to slider-first
  // since that's the safer default when we can't read the flag.
  const [journalFirst, setJournalFirst] = useState(true);
  // Extracted domain scores from today's brain dump. ``null`` = not yet
  // fetched; ``false`` = polled and gave up (silent fallback to plain
  // sliders); object = render the "Haiven read this as…" cards.
  const [extraction, setExtraction] = useState(null);
  const stepRef = useRef(null);

  // True when the most recent snapshot's local-bucketed window_end
  // matches today's local date. The dispatch's "completion flag" —
  // RCI snapshot existence-for-today IS the source of truth for
  // "user finished the daily flow." Drives the pre-fill skip + the
  // redirect-away guard below so a user with today's snapshot can't
  // be silently re-prompted into the flow (Jana / Jared May 19).
  const alreadyCheckedInToday = baseline?.window_end === todayKey();

  // Escape hatch for deliberate re-runs. Dashboard's "Check in again"
  // button passes ?force=1 so the user can explicitly redo today's
  // check-in; without the flag we redirect away to /protocol whenever
  // today's snapshot exists.
  const forceCheckin = searchParams.get("force") === "1";

  // Redirect away from /checkin once we know today is complete and
  // the caller didn't opt out via ?force=1. We wait for the baseline
  // fetch to resolve (baselineLoading false) so a fresh visitor with
  // no snapshot yet isn't bounced. Single-shot: the empty deps under
  // the conditions guarantee navigate fires at most once per mount.
  useEffect(() => {
    if (baselineLoading) return;
    if (!alreadyCheckedInToday) return;
    if (forceCheckin) return;
    if (dateParam) return;          // historical view handles itself above
    // Evening: /protocol would only bounce again into the guided ritual;
    // go there directly so the user sees one navigation, not two.
    navigate(timeWindow() === "evening" ? "/evening" : "/protocol", { replace: true });
  }, [baselineLoading, alreadyCheckedInToday, forceCheckin, dateParam, navigate]);

  useEffect(() => {
    if (!user) return;
    rciApi.snapshot(user.id, token)
      .then(snap => {
        setBaseline(snap);
        // Only seed domain scores from the baseline if the user hasn't
        // already touched them during the current in-flight check-in.
        setScores(prev => {
          if (saved?.touched?.BIO || saved?.touched?.PSYCH || saved?.touched?.BEH) {
            return prev;
          }
          return {
            BIO:   Math.max(1, Math.round((snap.bio_score   || 0) * 10)),
            PSYCH: Math.max(1, Math.round((snap.psych_score || 0) * 10)),
            BEH:   Math.max(1, Math.round((snap.beh_score   || 0) * 10)),
          };
        });
      })
      .catch(() => null)
      .finally(() => setBaselineLoading(false));

    // Wearable read is fire-and-forget — never blocks check-in. Two
    // independent calls so a 5xx on /oura/status doesn't suppress
    // displaying a row that already exists in wearable_daily.
    //   • status: gates the Oura-specific copy + auto-sync attempt.
    //   • today:  always tried — if a row exists for today it renders
    //     whatever fields are populated (HRV without sleep is fine).
    ouraApi.status(token)
      .then(status => { if (status?.connected) setOuraConnected(true); })
      .catch(() => {});

    ouraApi.today(token)
      .then(data => {
        if (!data) return;
        if (data.synced) {
          setWearableData(data);
        } else {
          // No row for today yet — kick a background sync so the rest
          // of the check-in can populate while we wait. Failure is
          // silent; the user proceeds with self-report only.
          ouraApi.sync(token)
            .then(synced => setWearableData({ synced: true, ...synced }))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate any prior brain_dump that was saved to the backend today —
  // in case localStorage was cleared but the user already submitted once
  // earlier in the day.
  //
  // Local-date contract end-to-end: we pass ``todayKey()`` to the GET so
  // the server queries the row stamped with the user's local YYYY-MM-DD
  // (which is also what ``persistBrainDump`` writes with). Without the
  // explicit date the server fell back to UTC ``date.today()``, which
  // bucketed evening submissions west of UTC under the *next* day —
  // and then re-rendered them as "today's" prefill on the actual next
  // morning, leaking yesterday's text into a fresh textarea. We also
  // keep the client-side gate ``row.date !== todayKey()`` as a belt-
  // and-braces check; any future change that drops the date param
  // would otherwise silently regress.
  //
  // Skip the rehydration entirely when the user has a snapshot for
  // today's window_end — they're not "resuming," they intentionally
  // hit "Check in again" from the Dashboard. The expected affordance
  // is a clean form, not silent re-population of this morning's text
  // (Jana saw her morning text pre-filled at midday and read it as
  // the app re-prompting her — the bug from May 19).
  useEffect(() => {
    if (!token) return;
    if (reflection) return;
    if (alreadyCheckedInToday) return;
    const today = todayKey();
    dailyCheckinApi.today(token, today)
      .then(row => {
        if (!row?.brain_dump) return;
        if (row.date !== today) return;
        setReflection(row.brain_dump);
      })
      .catch(() => {});
  }, [token, alreadyCheckedInToday]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-driven feature flag for journal-first vs slider-first. Read
  // once on mount; failures default to slider-first so a backend outage
  // can't accidentally show users a flow that depends on the
  // extraction service being live.
  useEffect(() => {
    featureFlagsApi.get()
      .then(flags => setJournalFirst(Boolean(flags?.journal_first_enabled)))
      .catch(() => setJournalFirst(false));
  }, []);

  // Resurface the most recent Evening Wind-Down intention on Step 0.
  // Silent on 404 — the card only renders when content is present.
  //
  // This deliberately does NOT filter by date. A previous strict
  // ``date === yesterday`` gate dropped the intention whenever the
  // wind-down was completed after local midnight (it buckets to today)
  // or whenever the user skipped a night — both silently, with no
  // empty state. ``resurfacedIntention`` instead always returns the
  // latest intention and carries its age in the label, so stale text
  // is framed honestly rather than withheld.
  useEffect(() => {
    if (!token) return;
    protocolInputsApi.recent("evening_intention", token)
      .then(row => setYesterdayIntention(resurfacedIntention(row)))
      .catch(() => setYesterdayIntention(null));
  }, [token]);

  // Pre-populate BIO from Oura readiness if available and untouched
  useEffect(() => {
    if (wearableData?.readiness != null && !touched.BIO) {
      setScores(prev => ({ ...prev, BIO: Math.max(1, Math.round(wearableData.readiness / 10)) }));
    }
  }, [wearableData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist resumable state on every relevant change. We skip this while
  // in the breathing phase (nothing to resume yet) and after final submit
  // (clearProgress handles that cleanup).
  useEffect(() => {
    if (phase !== "flow") return;
    saveProgress({ step, reflection, scores, touched, computeResult });
  }, [phase, step, reflection, scores, touched, computeResult]);

  // Journal-first extraction poll — fires once we land on Step 2 (the
  // domain confirmation step). Polls /checkin/extraction/today on a
  // bounded schedule so the cards can render with Haiven's read of the
  // brain dump. On null/out-of-range/timeout we silently fall back to
  // plain sliders by setting ``extraction`` to ``false`` (the absent-
  // sentinel the render path checks). This is the kill-switch escape
  // hatch the spec calls for — never blocks the user, never surfaces
  // an error state.
  useEffect(() => {
    if (!journalFirst) return;
    // Fire once the user has left the brain-dump step (step >= 2: the
    // Foundation page or the sliders). Gives extraction a head start so
    // the "Haiven read this as…" sliders are ready on step 3. The
    // ``extraction !== null`` guard below keeps it single-run.
    if (step < 2) return;
    if (!token) return;
    if (!reflection.trim()) {
      // No brain dump → slider-only is the correct path; don't poll.
      setExtraction(false);
      return;
    }
    if (extraction !== null) return;          // already resolved
    let cancelled = false;
    const POLL_INTERVALS_MS = [800, 1500, 1500, 2500];   // ~6.3s ceiling
    let i = 0;
    const tick = () => {
      if (cancelled) return;
      // Pass local date so the server matches the row written today
      // by ``persistBrainDump`` — extraction is keyed off the same
      // (user, local-date) bucket as the journal row itself.
      dailyCheckinApi.extractionToday(token, todayKey())
        .then(row => {
          if (cancelled) return;
          const scoresFromExt = extractionToScores(row);
          if (scoresFromExt) {
            setExtraction({ ...row, derived: scoresFromExt });
            // Promote the summary string up to the Step 3 surface so
            // we don't re-fetch the same row from the "Haiven noticed"
            // poll one screen later.
            if (row?.extraction_summary && !extractionSummary) {
              setExtractionSummary(row.extraction_summary);
            }
            setScores(prev => ({
              BIO:   touched.BIO   ? prev.BIO   : (scoresFromExt.BIO   ?? prev.BIO),
              PSYCH: touched.PSYCH ? prev.PSYCH : (scoresFromExt.PSYCH ?? prev.PSYCH),
              BEH:   touched.BEH   ? prev.BEH   : (scoresFromExt.BEH   ?? prev.BEH),
            }));
            return;
          }
          // No usable scores yet — keep polling until budget is gone.
          if (i < POLL_INTERVALS_MS.length) {
            setTimeout(tick, POLL_INTERVALS_MS[i++]);
          } else {
            setExtraction(false);
          }
        })
        .catch(() => {
          // 404 (background task hasn't completed) or any error — try
          // again until the budget runs out, then fall back silently.
          if (i < POLL_INTERVALS_MS.length) {
            setTimeout(tick, POLL_INTERVALS_MS[i++]);
          } else {
            setExtraction(false);
          }
        });
    };
    tick();
    return () => { cancelled = true; };
  }, [step, journalFirst, token, reflection, extraction, touched]);

  // "HAIVEN NOTICED" extraction poll — fires once, 1s after the user
  // lands on Step 3 (Signal Score). Step 3 is the natural pause point
  // where the card is actually seen. Prior implementation polled on
  // Step 1 advance but the user was always gone before the backend
  // finished its round-trip to Claude.
  useEffect(() => {
    if (step !== 4) return;
    if (!computeResult) return;          // wait for compute to complete
    if (!token) return;
    if (!reflection.trim()) return;      // no brain dump → nothing to extract
    if (extractionSummary) return;       // already have it; don't re-poll
    const t = setTimeout(() => {
      dailyCheckinApi.extractionToday(token, todayKey())
        .then(row => {
          if (row?.extraction_summary) {
            setExtractionSummary(row.extraction_summary);
          }
        })
        .catch(() => { /* 404 = not ready / not produced; render nothing */ });
    }, 1000);
    return () => clearTimeout(t);
  }, [step, computeResult, token, reflection, extractionSummary]);

  // Oura-branded copy is swapped for neutral wording in the native iOS
  // build while VITE_IOS_OURA_ENABLED is off (utils/features.js). The
  // data path is untouched: an existing connection still syncs and
  // still feeds BIO.
  const ouraUI = isOuraUIEnabled();
  const wearableName = ouraUI ? "Oura" : "Wearable";
  const bioLabel = wearableData
    ? [
        wearableData.hrv != null && `HRV ${Math.round(wearableData.hrv)}ms`,
        wearableData.sleep_score != null && `Sleep ${Math.round(wearableData.sleep_score)}`,
        wearableData.resting_hr != null && `Resting HR ${Math.round(wearableData.resting_hr)}bpm`,
        wearableData.readiness != null && `Readiness ${Math.round(wearableData.readiness)}`,
      ].filter(Boolean).join(" \u00b7 ") || `${wearableName} connected \u2014 no data yet`
    : ouraConnected
      ? `${wearableName} connected \u2014 syncing\u2026`
      : "No wearable connected \u2014 self-report only";

  const wearable = {
    BIO:   { detected: wearableData?.readiness != null ? Math.max(1, Math.round(wearableData.readiness / 10)) : (baseline ? Math.max(1, Math.round((baseline.bio_score || 0) * 10)) : 5), label: bioLabel },
    PSYCH: { detected: baseline ? Math.max(1, Math.round((baseline.psych_score || 0) * 10)) : 5, label: "No prior check-in yet" },
    BEH:   { detected: baseline ? Math.max(1, Math.round((baseline.beh_score || 0) * 10)) : 5, label: "No prior check-in yet" },
  };

  const baselineRci = presentedScore(baseline);
  const isCalibrating = !baseline || (baseline.total_snapshots != null && baseline.total_snapshots < 7);
  const rci = computeResult
    ? presentedScore(computeResult)
    : Math.round((scores.BIO + scores.PSYCH + scores.BEH) / 3 * 10);

  const advance = () => {
    setStep(s => s + 1);
    setTimeout(() => stepRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  // Jump to an explicit step index. Used by the reflection-step routing,
  // which skips the dedicated Foundation page (step 2) when no prompt is
  // due today and lands straight on the domain sliders (step 3).
  const goTo = (n) => {
    setStep(n);
    setTimeout(() => stepRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  // Persist the brain-dump text tied to today's check-in record. Runs on
  // advance from the reflection step; failure is non-blocking so the
  // user isn't trapped on the screen if the backend is cold-starting.
  //
  // Stamps the user's local YYYY-MM-DD so the server writes the row
  // against the day the user actually experienced. Without this, a
  // submit at 6pm Mountain (= next-day UTC) was stamped tomorrow and
  // then re-rendered as "today's prefill" the next morning. The hydrate
  // path above passes the same local date on the read side; both
  // surfaces stay in lockstep.
  const persistBrainDump = async () => {
    const text = reflection.trim();
    if (!text || !token) return;
    setSavingBrainDump(true);
    try {
      await dailyCheckinApi.upsert({ brain_dump: text, date: todayKey() }, token);
    } catch {
      /* non-blocking — localStorage still holds the text for resume */
    } finally {
      setSavingBrainDump(false);
    }
  };

  const advanceFromReflection = async () => {
    await persistBrainDump();
    // Foundation question is now its own page (step 2) when a prompt is
    // due today; otherwise skip straight to the domain sliders (step 3).
    // The "HAIVEN NOTICED" extraction card surfaces later on the Summary
    // step (4); polling happens in a useEffect tied to that step below.
    goTo(hasFoundationStep ? 2 : 3);
  };

  const skipReflection = () => {
    // Don't hit the DB on a skip — nothing to log.
    goTo(hasFoundationStep ? 2 : 3);
  };

  // Step-1 advance handlers. The Foundations prompt is no longer inlined
  // under the brain dump — it has its own dedicated page (step 2) — so
  // these are thin wrappers around the routing above.
  const continueFromReflection = async () => {
    await advanceFromReflection();
  };

  const skipFromReflection = async () => {
    skipReflection();
  };

  // ── Foundations: fetch today's depth prompt once on load ─────────────
  // Any failure or "already completed today" leaves the prompt null and
  // the check-in renders exactly as before — Foundations never blocks it.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    foundationsApi
      .getTodayPrompt(token)
      .then((res) => {
        if (cancelled) return;
        if (res && res.prompt && !res.completed_today) {
          setFoundationsPrompt(res.prompt);
        }
      })
      .catch((err) => console.error("Foundations today fetch failed:", err));
    return () => { cancelled = true; };
  }, [token]);

  const handleFoundationsComplete = async (responseValue) => {
    if (!foundationsPrompt) return;
    try {
      await foundationsApi.submitResponse(foundationsPrompt.day, responseValue, token);
    } catch (err) {
      // Never block the check-in — a failed save carries forward and
      // resurfaces on the next session.
      console.error("Foundations response submit failed:", err);
    } finally {
      setFoundationsComplete(true);
    }
  };

  // The Foundation question now occupies its own dedicated page (step 2),
  // between the brain dump (step 1) and the domain sliders (step 3),
  // regardless of whether it's a fresh or carried-forward prompt.
  // ``hasFoundationStep`` is keyed on the prompt EXISTING (not on
  // completion) so the progress dots stay stable (5 vs 4) the moment the
  // user answers it.
  const hasFoundationStep = !!foundationsPrompt;

  const submitAndAdvance = async () => {
    setComputeError("");
    setComputing(true);
    try {
      // Local-date contract. The previous version used
      // ``new Date().toISOString().split('T')[0]`` for window_end —
      // i.e. the UTC date — so a Mountain-time user submitting at
      // 7pm local stamped the snapshot's window_end as the *next*
      // UTC day. Dashboard then read snapshot.window_end against
      // the user's local "today" and the comparison silently
      // disagreed, surfacing "Begin today's check-in" right after
      // a successful submit. Use ``todayKey()`` everywhere so the
      // snapshot buckets against the user's calendar day.
      const today = todayKey();
      const start = new Date();
      start.setDate(start.getDate() - 14);
      const windowStart = localDateKeyFor(start);
      const payload = {
        user_id: user.id,
        window_start: windowStart,
        window_end: today,
        bio:   { i1: scores.BIO / 10 },
        psych: { i1: scores.PSYCH / 10 },
        beh:   { i1: scores.BEH / 10 },
        // Slider-override signal. When the user moved the slider for
        // a domain, that value IS the domain score. Untouched domains
        // let the backend defer to the journal extraction (unrounded)
        // when one is available.
        slider_touched: {
          bio:   !!touched.BIO,
          psych: !!touched.PSYCH,
          beh:   !!touched.BEH,
        },
      };
      // The felt-alignment self-report ("How today felt") moved to the
      // Evening Wind-Down ritual — the morning check-in no longer writes
      // daily_self_report. The snapshot's Values Alignment Index folds in
      // whatever the evening reflection recorded for the day.
      const res = await rciApi.compute(payload, token);
      setComputeResult(res);
      // Mark this device as "has submitted at least one check-in" so the
      // breathing intro is no longer forced on return visits.
      markEverSubmitted();
      advance();
    } catch {
      setComputeError("Taking a moment to connect. Try again in a few seconds.");
    } finally {
      setComputing(false);
    }
  };

  const updateScore = (code, val) => {
    setScores(prev => ({ ...prev, [code]: val }));
    setTouched(prev => ({ ...prev, [code]: true }));
  };

  // Where the check-in hands off when it's done. In the evening the
  // "protocol" is the guided wind-down ritual — /protocol would only
  // redirect there anyway, but landing in a full-screen ritual after
  // being promised "today's protocol" read as a surprise. Route (and
  // label, below) name the actual destination.
  const eveningHandoff = timeWindow() === "evening";
  const handoffRoute = eveningHandoff ? "/evening" : "/protocol";

  const finishCheckIn = () => {
    setSubmitted(true);
    // Best-effort: make sure any last brain-dump edits get written too.
    persistBrainDump();
    clearProgress();
    setTimeout(() => navigate(handoffRoute), 1500);
  };

  // Breathing intro — shown once per session before the first question.
  if (phase === "breathing") {
    return (
      <BreathingIntro
        onComplete={() => {
          setPhase("flow");
          saveProgress({ step: 0, reflection: "", scores, touched, computeResult: null });
        }}
      />
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="checkin" ref={stepRef}>

        {/* Ambient wash */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 360, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 50% 0%, rgba(158,132,86,0.08) 0%, transparent 65%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 48px" }}>

          {/* Header */}
          <div className="fu d1" style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 36,
          }}>
            <div>
              <Wordmark />
              <div style={{ fontSize: 13, color: P.textMute, fontWeight: 300 }}>
                Daily Check-in
              </div>
            </div>
            <ProgressDots
              step={hasFoundationStep ? step : (step >= 3 ? step - 1 : step)}
              total={hasFoundationStep ? 5 : 4}
            />
          </div>

          {/* STEP 0: RCI Overview */}
          {step === 0 && (
            <div className="step-enter">
              <div className="fu d1" style={{ textAlign: "center", marginBottom: 10 }}>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 13, fontStyle: "italic", color: P.textMute,
                  letterSpacing: "0.04em", marginBottom: 28,
                }}>
                  {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
                </div>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
                  <BigRing score={baselineRci} animate={true} indeterminate={isCalibrating} />
                </div>
              </div>

              <div className="fu d2" style={{ textAlign: "center", marginBottom: 32 }}>
                <h1 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 30, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.2, marginBottom: 12,
                }}>
                  {isCalibrating
                    ? "Building your baseline."
                    : baselineRci >= 75
                    ? "Strong signal today."
                    : baselineRci >= 50
                    ? "You\u2019re in a restoring state."
                    : baselineRci > 0
                    ? "Restoration is the work today."
                    : "Ready for your first check-in."}
                </h1>
                <p style={{ fontSize: 14, color: P.textMid, lineHeight: 1.7,
                  fontWeight: 300, maxWidth: 300, margin: "0 auto" }}>
                  {isCalibrating
                    ? `Haiven needs about a week to see your pattern. ${baseline?.total_snapshots || 0} check-in${(baseline?.total_snapshots || 0) !== 1 ? 's' : ''} so far.`
                    : baseline
                    ? `Your signal is at ${baselineRci}. Move each slider to match how things actually feel.`
                    : "Rate each domain as honestly as you can. Haiven needs a real baseline, not a good one."}
                </p>
              </div>

              {/* "Yesterday you said: [X]" — resurfaces the user's prior
                  Evening Wind-Down intention so the morning check-in can
                  ask how it went. Rendered before the wearable snapshot
                  and domain sliders per spec. */}
              {yesterdayIntention && (
                <div className="fu d3" style={{
                  marginBottom: 20,
                  padding: "16px 18px",
                  background: P.surfaceAlt,
                  border: `1px solid ${P.primary}`,
                  borderRadius: 14,
                  animation: "fadeIn 0.4s ease",
                }}>
                  <div style={{
                    fontSize: 9, color: P.primary,
                    letterSpacing: "0.18em", textTransform: "uppercase",
                    fontWeight: 500, marginBottom: 8,
                  }}>
                    {yesterdayIntention.label}
                  </div>
                  <p style={{
                    fontSize: 14, color: P.text, fontWeight: 300,
                    lineHeight: 1.65, fontStyle: "italic", marginBottom: 8,
                  }}>
                    {yesterdayIntention.text}
                  </p>
                  <p style={{
                    fontSize: 12, color: P.textMute, fontWeight: 300,
                    lineHeight: 1.5,
                  }}>
                    How did that go?
                  </p>
                </div>
              )}

              {/* Wearable snapshot \u2014 three rendering modes:
                    1. "No Oura data for today" \u2014 every overnight field
                       AND HRV is null. Replaces the four-row table
                       with a single muted italic line so an empty
                       state doesn't read as a stack of failures.
                    2. "Ring not worn" \u2014 backend flagged ``not_worn``
                       (no sleep session recorded that night at all).
                       Sleep / Resting HR / Readiness render as muted
                       italic instead of dashes; HRV still shows.
                    2b. "No sleep summary for this date" \u2014 ring WAS
                       worn (``not_worn === false``) but the night
                       produced no long_sleep-typed session, so Oura
                       never computed daily_sleep / daily_readiness.
                       Applies to Sleep + Readiness only.
                    3. Normal \u2014 at least one overnight field present.
                       Rows render values + arrows as before. */}
              {(() => {
                const w = wearableData;
                const noData =
                  w == null || (
                    w.hrv == null
                    && w.sleep_score == null
                    && w.readiness == null
                    && w.resting_hr == null
                  );
                const notWorn = !!w?.not_worn;
                // Explicit ``=== false`` \u2014 ``not_worn`` is trinary.
                // false means "ring confirmed worn"; null/undefined
                // means the backend hasn't resolved it yet and we must
                // NOT claim anything about the night.
                const wornConfirmed = w?.not_worn === false;
                const notWornNode = (
                  <span style={{
                    fontSize: 12, color: P.textMute,
                    fontStyle: "italic", fontWeight: 300,
                  }}>
                    Ring not worn
                  </span>
                );
                // Ring was worn but the night produced no
                // ``long_sleep`` session, so Oura never computed the
                // daily_sleep / daily_readiness summaries. Only fits
                // those two fields \u2014 Resting HR can arrive from the
                // heartrate endpoint independently of any summary.
                const noSummaryNode = (
                  <span style={{
                    fontSize: 12, color: P.textMute,
                    fontStyle: "italic", fontWeight: 300,
                  }}>
                    No sleep summary for this date
                  </span>
                );
                const cellValue = (raw, isOvernightField, isSummaryField) => {
                  if (raw != null) {
                    return { value: raw, italic: false };
                  }
                  if (notWorn && isOvernightField) {
                    return { value: notWornNode, italic: true };
                  }
                  if (wornConfirmed && isSummaryField) {
                    return { value: noSummaryNode, italic: true };
                  }
                  return { value: "\u2014", italic: false };
                };
                // Threshold-based direction + plain-language label for
                // each metric. Three states: up (good), down (under
                // load / light), flat (steady). Bands picked to match
                // common Oura interpretation guides \u2014 not personalized.
                const directionFor = (label, val) => {
                  if (val == null) return { arrow: "", text: "", good: false };
                  if (label === "HRV") {
                    if (val >= 50) return { arrow: "\u2191", text: "Well rested", good: true };
                    if (val < 30)  return { arrow: "\u2193", text: "Running low", good: false };
                    return { arrow: "\u2192", text: "Steady", good: true };
                  }
                  if (label === "Sleep") {
                    if (val >= 75) return { arrow: "\u2191", text: "Solid rest",  good: true };
                    if (val < 60)  return { arrow: "\u2193", text: "Light night", good: false };
                    return { arrow: "\u2192", text: "Steady", good: true };
                  }
                  if (label === "Resting HR") {
                    // Lower RHR reads as steadier; higher than usual as running higher.
                    if (val <= 55) return { arrow: "\u2193", text: "Steady", good: true };
                    if (val > 65)  return { arrow: "\u2191", text: "Higher than usual", good: false };
                    return { arrow: "\u2192", text: "Steady", good: true };
                  }
                  if (label === "Readiness") {
                    if (val >= 75) return { arrow: "\u2191", text: "Feeling good", good: true };
                    if (val < 60)  return { arrow: "\u2193", text: "Take it easy", good: false };
                    return { arrow: "\u2192", text: "Steady", good: true };
                  }
                  return { arrow: "", text: "", good: false };
                };
                const hrvDir   = directionFor("HRV",        w?.hrv);
                const sleepDir = directionFor("Sleep",      w?.sleep_score);
                const rhrDir   = directionFor("Resting HR", w?.resting_hr);
                const readyDir = directionFor("Readiness",  w?.readiness);
                const rows = [
                  { label: "HRV",        raw: w?.hrv != null ? `${Math.round(w.hrv)} ms` : null,
                    overnight: false, summary: false, ...hrvDir },
                  // ``summary: true`` = derived from Oura's daily_sleep
                  // / daily_readiness summaries, which only exist for a
                  // long_sleep-typed night. Resting HR is deliberately
                  // excluded — it can come from /heartrate too.
                  { label: "Sleep",      raw: w?.sleep_score != null ? `${Math.round(w.sleep_score)}` : null,
                    overnight: true,  summary: true,  ...sleepDir },
                  { label: "Resting HR", raw: w?.resting_hr != null ? `${Math.round(w.resting_hr)} bpm` : null,
                    overnight: true,  summary: false, ...rhrDir },
                  { label: "Readiness",  raw: w?.readiness != null ? `${Math.round(w.readiness)}` : null,
                    overnight: true,  summary: true,  ...readyDir },
                ];
                return (
                  <div className="fu d3" style={{
                    background: P.surface, borderRadius: 20,
                    padding: "20px 22px",
                    boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
                    marginBottom: 32,
                  }}>
                    <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                      textTransform: "uppercase", fontWeight: 500, marginBottom: 16 }}>
                      From your wearable today
                    </div>
                    {noData ? (
                      <p style={{
                        fontSize: 13, color: P.textMute, fontStyle: "italic",
                        fontWeight: 300, lineHeight: 1.6,
                      }}>
                        {ouraUI ? "No Oura data for today." : "No wearable data for today."}
                      </p>
                    ) : rows.map((item, i) => {
                      const { value } = cellValue(item.raw, item.overnight, item.summary);
                      return (
                        <div key={i} style={{
                          display: "flex", justifyContent: "space-between",
                          alignItems: "center",
                          paddingBottom: i < 3 ? 12 : 0,
                          marginBottom: i < 3 ? 12 : 0,
                          borderBottom: i < 3 ? `1px solid ${P.border}` : "none",
                        }}>
                          <span style={{ fontSize: 13, color: P.textMid, fontWeight: 300 }}>
                            {item.label}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 13, color: P.text, fontWeight: 400 }}>
                              {value}
                            </span>
                            <span style={{ fontSize: 12, color: item.good ? P.primary : P.textMute }}>
                              {item.arrow}
                            </span>
                            {item.text && (
                              <span style={{
                                fontSize: 11.5,
                                color: "#9E8456",
                                fontStyle: "italic",
                                fontWeight: 300,
                                whiteSpace: "nowrap",
                              }}>
                                {item.text}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="fu d4">
                <button className="btn-primary" onClick={advance}>
                  Begin today&rsquo;s check-in
                </button>
              </div>
            </div>
          )}

          {/* STEP 1: Brain dump */}
          {step === 1 && (() => {
            // Time-of-day-aware heading is the journal-first contract.
            // Slider-first keeps the original "What's on your mind"
            // copy so flipping the kill-switch is a true revert.
            const tod = journalFirst ? timeOfDayPrompt() : null;
            const kicker = tod ? tod.kicker : "Reflection";
            const headingNodes = tod
              ? tod.heading.split("\n").map((line, idx, arr) => (
                  <span key={idx}>{line}{idx < arr.length - 1 && <br />}</span>
                ))
              : (<>What&rsquo;s on your<br />mind right now?</>);
            return (
            <div className="step-enter">
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 10, color: P.primary, letterSpacing: "0.18em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 14 }}>
                  {kicker}
                </div>
                <h2 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 30, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.2, marginBottom: 12,
                }}>
                  {headingNodes}
                </h2>
                <p style={{ fontSize: 13, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.65 }}>
                  No filter. Just what&rsquo;s actually there.
                </p>
              </div>

              <textarea
                rows={8}
                placeholder="Write whatever comes — a feeling, a thought, a sentence fragment. This is yours."
                value={reflection}
                onChange={e => setReflection(e.target.value)}
                style={{ marginBottom: 10 }}
              />

              {/* Mic icon + dictation hint */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "2px 6px 14px",
              }}>
                <MicIcon />
                <span style={{
                  fontSize: 12, color: P.textMute, fontWeight: 300,
                  lineHeight: 1.4, fontStyle: "italic",
                }}>
                  You can also tap the mic on your keyboard to dictate
                </span>
              </div>

              {reflection.length > 0 && (
                <div style={{
                  fontSize: 11, color: P.textMute, textAlign: "right",
                  marginBottom: 24, animation: "fadeIn 0.3s ease",
                }}>
                  {reflection.length} characters
                </div>
              )}
              {reflection.length === 0 && <div style={{ marginBottom: 24 }} />}

              <div style={{ marginBottom: 14 }}>
                <button
                  className="btn-primary"
                  onClick={continueFromReflection}
                  disabled={savingBrainDump}
                  style={{ opacity: savingBrainDump ? 0.6 : 1 }}
                >
                  {savingBrainDump ? "Saving\u2026" : "Continue"}
                </button>
              </div>
              <div style={{ textAlign: "center" }}>
                <button className="btn-ghost" onClick={skipFromReflection}
                  style={{ border: "none", background: "none", color: P.textMute,
                    fontSize: 12, cursor: "pointer", padding: "8px" }}>
                  Skip for now
                </button>
              </div>
            </div>
            );
          })()}

          {/* STEP 2: Foundation question — its own dedicated page, shown
              only when a Foundations prompt is due today (otherwise the
              reflection step routes straight to the sliders at step 3). */}
          {step === 2 && (
            <div className="step-enter">
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, color: P.primary, letterSpacing: "0.18em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 14 }}>
                  Foundation
                </div>
                <h2 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 30, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.2, marginBottom: 12,
                }}>
                  One question before<br />you rate the day.
                </h2>
                <p style={{ fontSize: 13, color: P.textMid, fontWeight: 300, lineHeight: 1.65 }}>
                  This builds your Foundations profile — what actually matters to you.
                </p>
              </div>
              {foundationsPrompt && !foundationsComplete && (
                <FoundationsPrompt
                  prompt={foundationsPrompt}
                  onComplete={async (responseValue) => {
                    await handleFoundationsComplete(responseValue);
                    goTo(3);
                  }}
                />
              )}
              {/* Already answered this session (e.g. user tapped back) —
                  show a simple continue rather than the prompt again. */}
              {foundationsComplete && (
                <button className="btn-primary" onClick={() => goTo(3)}>
                  Continue
                </button>
              )}
            </div>
          )}

          {/* STEP 3: Domain Sliders (journal-first: Haiven read this as…) */}
          {step === 3 && (() => {
            // Journal-first surfaces Claude's read of the brain dump as
            // pre-filled sliders the user can override. We show the
            // "Haiven read this as…" framing only when:
            //   • the flag is on, AND
            //   • the user actually wrote something, AND
            //   • the extraction came back with at least one usable score.
            // Anything else falls back silently to the legacy header so
            // skipped or empty reflections, extraction failures, and
            // the kill-switch path all render the same plain sliders.
            const showExtractionFraming =
              journalFirst
              && reflection.trim().length > 0
              && extraction
              && typeof extraction === "object";
            const polling =
              journalFirst
              && reflection.trim().length > 0
              && extraction === null;
            return (
            <div className="step-enter">
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 10, color: P.primary, letterSpacing: "0.18em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 14 }}>
                  {showExtractionFraming ? "Haiven read this as" : "Your Domains"}
                </div>
                <h2 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 30, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.2, marginBottom: 10,
                }}>
                  {showExtractionFraming
                    ? <>Confirm — or<br />override.</>
                    : <>How are your<br />three domains today?</>}
                </h2>
                <p style={{ fontSize: 13, color: P.textMid, fontWeight: 300, lineHeight: 1.65 }}>
                  {showExtractionFraming
                    ? "These are pulled from what you just wrote. Adjust any that don’t feel right."
                    : polling
                      ? "Reading what you wrote… adjust each to where you are now."
                      : "Start from where you were. Adjust to where you are now."}
                </p>
              </div>

              {DOMAINS.map(domain => (
                <DomainSlider
                  key={domain.code}
                  domain={domain}
                  value={scores[domain.code]}
                  onChange={val => updateScore(domain.code, val)}
                  wearable={wearable[domain.code]}
                  isBaseline={!baseline}
                  touched={touched[domain.code]}
                />
              ))}

              {/* "How today felt" (felt-alignment self-report) moved to the
                  Evening Wind-Down ritual — no longer part of the morning
                  check-in. The Foundation question now lives on its own
                  dedicated page (step 2) above. */}

              {computeError && (
                <p style={{ fontSize: 13, color: "#9E8456", fontWeight: 300, textAlign: "center", marginTop: 8 }}>
                  {computeError}
                </p>
              )}
              <div style={{ marginTop: 8 }}>
                <button className="btn-primary" onClick={submitAndAdvance}
                  disabled={computing}
                  style={{ opacity: computing ? 0.6 : 1 }}>
                  {computing ? "Computing\u2026" : "These look right"}
                </button>
              </div>
            </div>
            );
          })()}

          {/* STEP 4: Summary */}
          {step === 4 && (
            <div className="step-enter">
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 13, fontStyle: "italic", color: P.textMute,
                  marginBottom: 28,
                }}>
                  Your signal today
                </div>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
                  <BigRing score={rci} animate={true} />
                </div>
              </div>

              <div style={{ textAlign: "center", marginBottom: 32 }}>
                <h2 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 28, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.25, marginBottom: 12,
                }}>
                  {computeResult?.coherence_status === 'STABLE_COHERENCE' ? "Strong signal today."
                    : computeResult?.coherence_status === 'STABILIZING' ? "Steadying."
                    : computeResult?.coherence_status === 'REGRESSING' ? "Something's pulling things off."
                    : rci >= 75 ? "Strong signal today."
                    : rci >= 60 ? "Steadying."
                    : "Signal scattered."}
                </h2>
                <p style={{ fontSize: 13.5, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.7, maxWidth: 300, margin: "0 auto" }}>
                  {reflection.length > 0
                    ? "Your reflection is here. Patterns will emerge over time."
                    : "Check-in received. More will come into view as you keep showing up."}
                </p>
              </div>

              {/* HAIVEN NOTICED — semantic extraction of today's brain dump.
                  Appears below the Signal Score after a 1s poll. Renders
                  only when the backend has produced a summary; silent
                  otherwise (no spinner, no error). */}
              {extractionSummary && (
                <div style={{
                  marginBottom: 28,
                  padding: "16px 18px",
                  background: P.surfaceAlt,
                  border: `1px solid ${P.primary}`,
                  borderRadius: 14,
                  animation: "fadeIn 0.4s ease",
                }}>
                  <div style={{
                    fontSize: 9, color: P.primary,
                    letterSpacing: "0.18em", textTransform: "uppercase",
                    fontWeight: 500, marginBottom: 8,
                  }}>
                    Haiven noticed
                  </div>
                  <p style={{
                    fontSize: 13.5, color: P.text, fontWeight: 300,
                    lineHeight: 1.65, fontStyle: "italic",
                  }}>
                    {extractionSummary}
                  </p>
                </div>
              )}

              {/* Domain summary row */}
              <div style={{
                background: P.surface, borderRadius: 20,
                padding: "20px 22px",
                boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
                marginBottom: 28,
              }}>
                <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 16 }}>
                  Today&rsquo;s Domains
                </div>
                {DOMAINS.map((d, i) => (
                  <div key={d.code} style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: i < 2 ? 14 : 0,
                    marginBottom: i < 2 ? 14 : 0,
                    borderBottom: i < 2 ? `1px solid ${P.border}` : "none",
                  }}>
                    <div>
                      <div style={{ fontSize: 9, color: P.primary, letterSpacing: "0.14em",
                        textTransform: "uppercase", fontWeight: 500, marginBottom: 2 }}>
                        {DOMAIN_WORD[d.code] || d.code}
                      </div>
                      <div style={{ fontSize: 13, color: P.textMid,
                        fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>
                        {d.label}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {(() => {
                        const sk = { BIO: 'bio_score', PSYCH: 'psych_score', BEH: 'beh_score' };
                        const val = computeResult
                          ? Math.max(1, Math.round((computeResult[sk[d.code]] || 0) * 100))
                          : scores[d.code] * 10;
                        return (
                          <>
                            <div style={{ width: 72, height: 3, background: P.surfaceAlt, borderRadius: 99 }}>
                              <div style={{
                                height: "100%", width: `${val}%`,
                                background: `linear-gradient(90deg, ${P.primary}, ${P.primaryMid})`,
                                borderRadius: 99,
                              }} />
                            </div>
                            <span style={{
                              fontFamily: "'Playfair Display', serif",
                              fontSize: 18, fontStyle: "italic", color: P.text, minWidth: 28,
                              textAlign: "right",
                            }}>
                              {val}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Foundation Alignment — Layer 3. Shown when the snapshot
                  carried a Values Alignment Index back (folded in from the
                  day's "How today felt" evening reflection). Closes the
                  loop: a tracked number, with a trend arrow once ~2 weeks
                  of history exists. */}
              {computeResult?.values_alignment != null && (() => {
                const vaiPct = Math.round(computeResult.values_alignment * 100);
                const trend = computeResult.values_alignment_trend;
                const trendNode = trend == null
                  ? null
                  : trend > 0.005
                    ? { arrow: "↑", text: "toward your foundation", color: P.primary }
                    : trend < -0.005
                      ? { arrow: "↓", text: "drifting from your foundation", color: "#9E8456" }
                      : { arrow: "→", text: "holding steady", color: P.textMute };
                return (
                  <div style={{
                    background: P.surface, borderRadius: 20, padding: "20px 22px",
                    boxShadow: "0 1px 16px rgba(28,24,20,0.07)", marginBottom: 28,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                        textTransform: "uppercase", fontWeight: 500 }}>
                        Foundation Alignment
                      </div>
                      <span style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 26, fontStyle: "italic", color: P.text, lineHeight: 1,
                      }}>
                        {vaiPct}
                      </span>
                    </div>
                    <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300,
                      lineHeight: 1.6, marginTop: 8 }}>
                      How closely today tracked what you said matters most.
                    </p>
                    {trendNode && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                        <span style={{ fontSize: 13, color: trendNode.color }}>{trendNode.arrow}</span>
                        <span style={{ fontSize: 11.5, color: trendNode.color, fontStyle: "italic", fontWeight: 300 }}>
                          {trendNode.text}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {computeResult?.bio_blend_active && (
                <p style={{
                  fontSize: 11, color: P.textMute, fontStyle: "italic",
                  textAlign: "center", marginBottom: 20, lineHeight: 1.5,
                }}>
                  {ouraUI
                    ? "Body signal includes both Oura and self-report"
                    : "Body signal includes both wearable data and self-report"}
                </p>
              )}

              <div style={{ marginBottom: 14 }}>
                <button className="btn-primary" onClick={finishCheckIn}>
                  {submitted ? "Saved \u2714" : "Complete check-in"}
                </button>
              </div>

              {submitted && (
                <div style={{
                  textAlign: "center", padding: "14px",
                  animation: "fadeUp 0.4s ease forwards",
                }}>
                  <p style={{ fontSize: 13, color: P.textMute, fontWeight: 300, lineHeight: 1.6 }}>
                    {eveningHandoff
                      ? "Check-in complete. Your evening wind-down is ready."
                      : "Check-in complete. Your protocol for today is ready."}
                  </p>
                  <button onClick={() => navigate(handoffRoute)} style={{
                    marginTop: 12, background: "none", border: "none",
                    color: P.primary, fontSize: 13, cursor: "pointer",
                    fontWeight: 500, letterSpacing: "0.04em",
                  }}>
                    {eveningHandoff ? "Begin your wind-down →" : "View today’s protocol →"}
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
